import { useEffect, useState } from 'react';
import { useMedia } from 'react-use';

import { ConnectEthereumWalletButton } from '@/components/ConnectEthereumWalletButton';

import { useCheckoutMode } from '@/hooks/useCheckoutMode';
import { useMatchaWallets } from '@/hooks/useMatchaWallets';

import { fontStyles } from '@/styles/typography';
import { breakpoints } from '@/styles/util';

import { Panel } from '@/ui/Panel';

import { TokenResult } from '@/utils/0x/token-registry.types';
import { classNames } from '@/utils/classnames';
import { EthereumAddress, SolanaAddress } from '@/utils/models';

import { LimitOrdersPanel } from './LimitOrdersPanel';
import { TradeLimitChart } from './TradeLimitChart';

interface TradeLimitContentProps {
  sellToken: TokenResult | undefined;
  buyToken: TokenResult | undefined;
}

export function TradeLimitContent({ sellToken, buyToken }: TradeLimitContentProps) {
  const { ethereumWallet, solanaWallet } = useMatchaWallets();
  const [checkoutMode] = useCheckoutMode();

  const [isExpanded, setIsExpanded] = useState(false);

  const isDesktop = useMedia(breakpoints.desktop, false);
  const isLimitMode = checkoutMode === 'advanced';

  const [loadChartWidget, setLoadChartWidget] = useState(false);

  // Ensure we don't animate the chart when the page first loads.
  const [isFirstLoad, setIsFirstLoad] = useState(true);
  useEffect(() => {
    if (isLimitMode && isDesktop && isFirstLoad) {
      setLoadChartWidget(true);
      setIsFirstLoad(false);
    }
  }, [isLimitMode, isDesktop, isFirstLoad]);

  return (
    /* Left column: chart (desktop only) with the orders panel stacked
       beneath it. `empty:hidden` keeps it out of the flex layout (and its
       gap) in market mode, when the chart renders null and there's no
       panel. `order-2` drops it below the trade module on mobile, where
       the chart is hidden and only the panel shows. */
    <div
      className={classNames(
        'order-2 desktop:order-1 flex flex-col gap-3 empty:hidden desktop:transition-[width,opacity] desktop:duration-300 desktop:ease-out',
        {
          'starting:w-0 starting:opacity-0': !isFirstLoad,
          'w-full opacity-100': isLimitMode,
          'w-0 opacity-0': !isLimitMode,
          'max-w-4xl': !isExpanded,
        },
      )}
      onTransitionStart={(e) => {
        if (e.target === e.currentTarget && e.propertyName === 'width') {
          setLoadChartWidget(false);
        }
      }}
      onTransitionEnd={(e) => {
        if (
          e.target === e.currentTarget &&
          e.propertyName === 'width' &&
          e.currentTarget.clientWidth > 0
        ) {
          setLoadChartWidget(isDesktop);
        }
      }}
    >
      <TradeLimitChart
        className="hidden desktop:block"
        canLoadWidget={loadChartWidget}
        buyToken={buyToken}
        sellToken={sellToken}
        isExpanded={isExpanded}
        setIsExpanded={setIsExpanded}
      />

      {isLimitMode ? (
        ethereumWallet || solanaWallet ? (
          <LimitOrdersPanel
            funders={{
              ethereum: ethereumWallet?.address as EthereumAddress | undefined,
              solana: solanaWallet?.address as SolanaAddress | undefined,
            }}
            sellToken={sellToken}
            buyToken={buyToken}
            className="w-full"
          />
        ) : (
          <Panel className="w-full min-h-[340px] flex flex-col gap-4 items-center justify-center">
            <p
              className={fontStyles({
                variant: 'LABEL_LG',
                class: ['flex', 'justify-center', 'mb-2', 'text-center'],
              })}
            >
              Connect your wallet to see your limit orders.
            </p>

            <ConnectEthereumWalletButton source="trade_module" />
          </Panel>
        )
      ) : null}
    </div>
  );
}
